import React, { useState, useEffect } from 'react';
import { schedulerAPI } from '../services/api';
import {
  Clock,
  Play,
  Activity,
  AlertCircle,
  CheckCircle,
  XCircle,
  Calendar,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const SchedulerManagement = () => {
  const { hasPermission, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('status');
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [runningManual, setRunningManual] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50
  });

  // Allow managers, system admins, developers, and any admin role to trigger
  // FORCE TRUE for debugging as requested
  const canTrigger = true;
  /* 
  const canTrigger = hasPermission('manager_access') || 
                     hasPermission('system_admin') || 
                     hasRole('developer') || 
                     hasRole('admin');
  */

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    } else if (activeTab === 'status') {
      fetchStatus();
    } else if (activeTab === 'health') {
      fetchHealth();
    }
  }, [activeTab, pagination.page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await schedulerAPI.getLogs({
        page: pagination.page,
        limit: pagination.limit,
        logType: 'scheduler'
      });
      setLogs(response.data.data.logs);
      setPagination(prev => ({
        ...prev,
        ...response.data.data.pagination
      }));
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load scheduler logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await schedulerAPI.getStatus();
      setStatus(response.data.data.events);
    } catch (error) {
      console.error('Error fetching status:', error);
      toast.error('Failed to load scheduler status');
    } finally {
      setLoading(false);
    }
  };

  const fetchHealth = async () => {
    try {
      setLoading(true);
      const response = await schedulerAPI.getHealth();
      setHealth(response.data.data);
    } catch (error) {
      console.error('Error fetching health:', error);
      toast.error('Failed to load scheduler health');
    } finally {
      setLoading(false);
    }
  };

  const handleManualTrigger = async () => {
    if (!canTrigger) {
      toast.error('You do not have permission to manually trigger the scheduler');
      return;
    }

    if (!confirm('Are you sure you want to manually trigger subscription order creation? This will create orders from pending schedules.')) {
      return;
    }

    try {
      setRunningManual(true);
      const response = await schedulerAPI.runNow();
      toast.success(response.data.message);

      // Show execution details
      const responseData = response.data;
      const createdCount = responseData.ordersCreated || 0;
      toast.success(`Created ${createdCount} orders successfully`, {
        duration: 5000
      });

      // Refresh data
      fetchStatus();
      fetchHealth();
      if (activeTab === 'logs') {
        fetchLogs();
      }
    } catch (error) {
      console.error('Error running manual trigger:', error);
      toast.error(error.response?.data?.message || 'Failed to trigger scheduler');
    } finally {
      setRunningManual(false);
    }
  };

  const refreshData = () => {
    if (activeTab === 'logs') {
      fetchLogs();
    } else if (activeTab === 'status') {
      fetchStatus();
    } else if (activeTab === 'health') {
      fetchHealth();
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Scheduler Management</h1>
          <p className="text-gray-600 mt-1">Monitor and manage automated subscription order creation</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={refreshData}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          {canTrigger && (
            <div className="relative">
              <button
                onClick={handleManualTrigger}
                disabled={runningManual}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${health?.pendingSchedulesToday > 0
                  ? 'bg-orange-600 text-white hover:bg-orange-700 animate-pulse'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
              >
                <Play className="w-4 h-4" />
                {runningManual ? 'Running...' : 'Manual Trigger'}
              </button>
              {health?.pendingSchedulesToday > 0 && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                  {health.pendingSchedulesToday}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('status')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'status'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Event Status
              </div>
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'logs'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Execution Logs
              </div>
            </button>
            <button
              onClick={() => setActiveTab('health')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'health'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Health Check
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {activeTab === 'status' && status && (
                <StatusTab events={status} />
              )}
              {activeTab === 'logs' && (
                <LogsTab
                  logs={logs}
                  pagination={pagination}
                  onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                />
              )}
              {activeTab === 'health' && health && (
                <HealthTab health={health} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Status Tab
const StatusTab = ({ events }) => {
  return (
    <div className="space-y-4">
      {events.map((event, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-900">{event.eventName}</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${event.status === 'ENABLED'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
                  }`}>
                  {event.status}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 font-medium">Schedule</p>
                  <p className="text-gray-900">{event.schedule}</p>
                </div>
                <div>
                  <p className="text-gray-600 font-medium">Last Executed</p>
                  <p className="text-gray-900">
                    {event.lastExecuted
                      ? new Date(event.lastExecuted).toLocaleString()
                      : 'Never'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 font-medium">Next Execution</p>
                  <p className="text-gray-900">
                    {event.nextExecution
                      ? new Date(event.nextExecution).toLocaleString()
                      : 'Not scheduled'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 font-medium">Time Until Next Run</p>
                  <p className="text-gray-900">
                    {event.nextExecution
                      ? getTimeUntil(event.nextExecution)
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className="ml-4">
              {event.status === 'ENABLED' ? (
                <CheckCircle className="w-8 h-8 text-green-500" />
              ) : (
                <XCircle className="w-8 h-8 text-red-500" />
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Logs Tab
const LogsTab = ({ logs, pagination, onPageChange }) => {
  if (logs.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 text-lg">No scheduler logs found</p>
        <p className="text-gray-500 text-sm mt-2">Logs will appear here after scheduler executions</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Logs List */}
      <div className="space-y-2">
        {logs.map((log) => (
          <div
            key={log.id}
            className={`border rounded-lg p-4 ${log.logType === 'error'
              ? 'border-red-200 bg-red-50'
              : 'border-gray-200 bg-white'
              }`}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {log.logType === 'error' ? (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                  <p className="font-medium text-gray-900">{log.message}</p>
                </div>
                {log.details && (
                  <p className="mt-2 text-sm text-gray-600">{log.details}</p>
                )}
              </div>
              <div className="text-right ml-4">
                <p className="text-sm text-gray-500">
                  {new Date(log.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalRecords} total logs)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Health Tab
const HealthTab = ({ health }) => {
  const isHealthy = health.eventSchedulerStatus === 'ON' && health.systemStatus === 'healthy';

  return (
    <div className="space-y-6">
      {/* Overall Health Status */}
      <div className={`border-2 rounded-lg p-6 ${isHealthy
        ? 'border-green-500 bg-green-50'
        : 'border-red-500 bg-red-50'
        }`}>
        <div className="flex items-center gap-4">
          {isHealthy ? (
            <CheckCircle className="w-12 h-12 text-green-500" />
          ) : (
            <AlertCircle className="w-12 h-12 text-red-500" />
          )}
          <div>
            <h3 className="text-2xl font-bold text-gray-900">
              System Status: {health.systemStatus.toUpperCase()}
            </h3>
            <p className="text-gray-600 mt-1">
              Event Scheduler is {health.eventSchedulerStatus}
            </p>
          </div>
        </div>
      </div>

      {/* Pending Schedules */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pending Delivery Schedules</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-blue-600 font-medium">Today (IST)</p>
                <p className="text-2xl font-bold text-blue-900">{health.pendingSchedulesToday ?? 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-green-600 font-medium">Tomorrow (IST)</p>
                <p className="text-2xl font-bold text-green-900">{health.pendingSchedulesTomorrow ?? 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-sm text-purple-600 font-medium">Orders Created Today</p>
                <p className="text-2xl font-bold text-purple-900">{health.ordersCreatedToday ?? 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Last Order Creation */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Last Order Creation</h3>
        <div className="flex items-center gap-4">
          <Clock className="w-8 h-8 text-gray-400" />
          <div>
            <p className="text-gray-600">Last successful order creation run</p>
            <p className="text-lg font-semibold text-gray-900">
              {health.lastOrderCreation
                ? new Date(health.lastOrderCreation).toLocaleString()
                : 'Never'}
            </p>
            {health.lastOrderCreation && (
              <p className="text-sm text-gray-500 mt-1">
                {getTimeAgo(health.lastOrderCreation)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Warnings */}
      {!isHealthy && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-red-900 mb-2">System Health Issues Detected</h4>
              <ul className="list-disc list-inside text-red-800 space-y-1">
                {health.eventSchedulerStatus !== 'ON' && (
                  <li>Event scheduler is not running. Contact system administrator.</li>
                )}
                {health.systemStatus !== 'healthy' && (
                  <li>System health check failed. Review scheduler logs for details.</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Functions
const getTimeUntil = (dateString) => {
  const now = new Date();
  const target = new Date(dateString);
  const diff = target - now;

  if (diff < 0) return 'Overdue';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ${hours % 24} hour${hours % 24 !== 1 ? 's' : ''}`;
  }

  return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
};

const getTimeAgo = (dateString) => {
  const now = new Date();
  const past = new Date(dateString);
  const diff = now - past;

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }

  if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  }

  return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
};

export default SchedulerManagement;